import {
  sendAccordionInteraction,
  sendProductVariantChange,
  sendSingleProductView,
} from '@nulo/gtm';

/** @type {string[]} */
const DEFAULT_PDP_SECTION_KEYS = [
  'product_media_gallery',
  'product_details',
  'product_related_products',
];

/** @type {boolean} */
let isPdpSwitching = false;

/** @type {boolean} */
let pdpVariantSwitchBound = false;

/**
 * JSON template section keys (e.g. product_details) map to DOM ids like
 * template--123__product_details. The Section Rendering API requires those full ids.
 *
 * @param {string} sectionKey
 * @returns {string | null}
 */
function resolvePdpSectionId(sectionKey) {
  const root = document.getElementById('MainContent');
  if (!root || !sectionKey) return null;

  const suffix = `__${sectionKey}`;
  const sectionEl = root.querySelector(`.shopify-section[id$="${suffix}"]`);
  if (!(sectionEl instanceof HTMLElement) || !sectionEl.id) return null;

  return sectionEl.id.replace(/^shopify-section-/, '');
}

/**
 * @param {string[]} sectionKeys
 * @returns {string[]}
 */
function resolvePdpSectionIds(sectionKeys) {
  const ids = [];

  for (const key of sectionKeys) {
    const id = resolvePdpSectionId(key);
    if (id) ids.push(id);
  }

  return ids;
}

/**
 * @param {HTMLElement} selector
 * @returns {string[]}
 */
function getPdpSectionKeys(selector) {
  const raw = selector.dataset.sectionKeys || DEFAULT_PDP_SECTION_KEYS.join(',');
  return raw
    .split(',')
    .map((key) => key.trim())
    .filter(Boolean);
}

function getCurrentPdpSize() {
  const currentSize = document.querySelector(
    '.product-variant-selector__option--current .product-variant-selector__size'
  );
  return currentSize?.textContent?.trim() || '';
}

/**
 * @returns {Record<string, unknown> | null}
 */
function parsePdpGtmConfig() {
  const el = document.querySelector('[data-product-gtm]');
  if (!el?.textContent) return null;

  try {
    return JSON.parse(el.textContent);
  } catch {
    return null;
  }
}

function rebindPriceSpider() {
  // Use a double rAF so PriceSpider.rebind() runs after the morphed DOM
  // has fully painted. A single rAF can fire before the browser flushes
  // the new .ps-widget into the render tree, leaving the button blank.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (window.PriceSpider?.rebind) {
        window.PriceSpider.rebind();
      }
    });
  });
}

function reinitPdpWebComponents() {
  for (const el of document.querySelectorAll('product-media-gallery, product-sticky-cta')) {
    if (typeof el.reinit === 'function') {
      el.reinit();
    }
  }

  const reinitRelated = () => {
    for (const el of document.querySelectorAll('product-related-products')) {
      if (!el.isConnected) continue;
      if (typeof el.reinit === 'function') {
        el.reinit();
      }
    }
  };

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(reinitRelated, { timeout: 2000 });
  } else {
    window.setTimeout(reinitRelated, 1);
  }
}

/**
 * Build a same-origin product path. request.locale.root_url is often "/" for the
 * default market — concatenating "/products/..." would produce "//products/..."
 * (protocol-relative URL → https://products/...).
 *
 * @param {string} localeRoot
 * @param {string} productHandle
 * @returns {string}
 */
function buildProductPath(localeRoot, productHandle) {
  const root =
    localeRoot && localeRoot !== '/' ? String(localeRoot).replace(/\/$/, '') : '';
  return `${root}/products/${productHandle}`;
}

/**
 * @param {string} localeRoot
 * @param {string} productHandle
 * @returns {URL}
 */
function buildProductUrl(localeRoot, productHandle) {
  return new URL(buildProductPath(localeRoot, productHandle), window.location.origin);
}

/**
 * Batch Section Rendering (?sections=) can return product sections without a
 * product object (title/product_id null). A full product page fetch matches
 * a normal PDP load and always includes product context.
 *
 * @param {URL} productUrl
 * @param {string[]} sectionIds
 * @returns {Promise<Record<string, string> | null>}
 */
async function fetchPdpSectionsFromPage(productUrl, sectionIds) {
  const { buildSectionSelector, normalizeSectionId } = await import('@theme/section-renderer');

  const response = await fetch(productUrl.toString(), {
    credentials: 'same-origin',
    headers: { Accept: 'text/html' },
  });
  if (!response.ok) return null;

  const pageHtml = await response.text();
  const doc = new DOMParser().parseFromString(pageHtml, 'text/html');
  /** @type {Record<string, string>} */
  const sections = {};

  for (const sectionId of sectionIds) {
    const normalizedId = normalizeSectionId(sectionId);
    const el = doc.getElementById(buildSectionSelector(normalizedId));
    if (el?.outerHTML) {
      sections[sectionId] = el.outerHTML;
    }
  }

  return sections;
}

/**
 * @param {string} html
 * @returns {boolean}
 */
function isValidProductDetailsHtml(html) {
  if (!html) return false;

  if (html.includes('"title": null') || html.includes('"product_id": null')) {
    return false;
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const title = doc.querySelector('.product-details__title');
  return title instanceof HTMLElement && Boolean(title.textContent?.trim());
}

/**
 * @param {string} html
 * @returns {boolean}
 */
function shouldMorphRelatedSection(html) {
  if (!html) return false;
  return html.includes('id="related-products"');
}

/**
 * @param {string} handle
 * @param {HTMLElement} selector
 * @param {string} nextSize
 * @param {string} [variantFrom]
 */
async function switchPdpProduct(handle, selector, nextSize, variantFrom = '') {
  if (isPdpSwitching) return;

  const gtmConfig = parsePdpGtmConfig();
  const sizeFrom = variantFrom || getCurrentPdpSize();

  isPdpSwitching = true;
  selector.setAttribute('aria-busy', 'true');

  try {
    const localeRoot = selector.dataset.localeRoot || '';
    const sectionKeys = getPdpSectionKeys(selector);
    const sectionIds = resolvePdpSectionIds(sectionKeys);

    if (sectionIds.length === 0) return;

    const productUrl = buildProductUrl(localeRoot, handle);
    const sections = await fetchPdpSectionsFromPage(productUrl, sectionIds);
    if (!sections) return;

    const detailsSectionId = sectionIds.find((id) => id.includes('product_details'));
    if (detailsSectionId && !isValidProductDetailsHtml(sections[detailsSectionId])) {
      return;
    }

    const { morphSection, normalizeSectionId, buildSectionSelector } = await import(
      '@theme/section-renderer'
    );

    for (const sectionId of sectionIds) {
      const html = sections[sectionId];
      if (!html) continue;

      if (sectionId.includes('product_related_products') && !shouldMorphRelatedSection(html)) {
        const normalizedId = normalizeSectionId(sectionId);
        const existing = document.getElementById(buildSectionSelector(normalizedId));
        if (existing) existing.innerHTML = '';
        continue;
      }

      const normalizedId = normalizeSectionId(sectionId);
      if (!document.getElementById(buildSectionSelector(normalizedId))) continue;

      await morphSection(normalizedId, html);
    }

    const parentHandle = selector.dataset.parentHandle;
    if (parentHandle) {
      window.history.replaceState({}, '', buildProductPath(localeRoot, parentHandle));
    }

    reinitPdpWebComponents();
    rebindPriceSpider();

    const nextSelector = document.querySelector('[data-variant-selector]');
    const currentSize = nextSelector?.querySelector(
      '.product-variant-selector__option--current .product-variant-selector__size'
    );
    const variantTo = currentSize?.textContent?.trim() || nextSize;

    const nextGtm = parsePdpGtmConfig() || gtmConfig;

    if (variantTo && sizeFrom && variantTo !== sizeFrom) {
      sendProductVariantChange({
        variant_from: sizeFrom,
        variant_to: variantTo,
        variant_attribute: 'size',
        ecommerce: {
          items: [
            {
              item_id: nextGtm?.product_id,
              item_name: nextGtm?.title,
              item_brand: nextGtm?.vendor,
              item_variant: variantTo,
              item_category: nextGtm?.item_category,
              item_category2: nextGtm?.item_category2,
              price: 0,
              quantity: 1,
              index: 0,
            },
          ],
        },
      });
    }

    if (currentSize instanceof HTMLElement) {
      currentSize.focus();
      currentSize.setAttribute('tabindex', '-1');
    }
  } finally {
    isPdpSwitching = false;
    document.querySelector('[data-variant-selector]')?.removeAttribute('aria-busy');
  }
}

function bindPdpVariantSwitch() {
  if (pdpVariantSwitchBound) return;
  pdpVariantSwitchBound = true;

  document.addEventListener('click', (event) => {
    const root = document.getElementById('MainContent');
    if (!(root instanceof HTMLElement) || root.dataset.template !== 'product') return;

    const link = event.target.closest('[data-product-handle]');
    if (!(link instanceof HTMLElement)) return;

    const selector = link.closest('[data-variant-selector]');
    if (!(selector instanceof HTMLElement)) return;

    const handle = link.dataset.productHandle;
    if (!handle || handle === selector.dataset.currentHandle) return;

    event.preventDefault();
    switchPdpProduct(handle, selector, link.textContent?.trim() || '');
  });
}

/**
 * @extends {HTMLElement}
 */
class ProductDetails extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    if (this.#abortController) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const gtmConfig = this.#parseGtmConfig();
    if (gtmConfig) {
      sendSingleProductView(gtmConfig);
    }

    bindPdpVariantSwitch();
    this.#initPriceSpider();
    this.#watchSkuChanges();
    this.#bindAccordionTracking(signal, gtmConfig);
    this.#bindSkipLink(signal);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  #parseGtmConfig() {
    const el = this.querySelector('[data-product-gtm]');
    if (!el?.textContent) return null;

    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  /**
   * @param {AbortSignal} signal
   */
  #bindSkipLink(signal) {
    const skipLink = document.querySelector('.product-pdp__skip-link');
    const main = document.getElementById('product-main');
    if (!(skipLink instanceof HTMLAnchorElement) || !(main instanceof HTMLElement)) return;

    skipLink.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        main.focus();
      },
      { signal }
    );
  }

  /**
   * @param {AbortSignal} signal
   * @param {Record<string, unknown> | null} gtmConfig
   */
  #bindAccordionTracking(signal, gtmConfig) {
    const accordion = this.querySelector('.product-accordion');
    if (!accordion) return;

    const items = accordion.querySelectorAll('.product-accordion__item');
    items.forEach((item, index) => {
      if (!(item instanceof HTMLDetailsElement)) return;

      item.addEventListener(
        'toggle',
        () => {
          const title = item.querySelector('.product-accordion__title');
          const accordionName = title?.textContent?.trim().toLowerCase() || '';
          if (!accordionName) return;

          sendAccordionInteraction({
            accordionName,
            accordionState: item.open ? 'open' : 'closed',
            accordionPosition: index + 1,
            ecommerce: {
              items: [
                {
                  item_id: gtmConfig?.product_id,
                  item_name: gtmConfig?.title,
                  item_variant: getCurrentPdpSize(),
                },
              ],
            },
          });
        },
        { signal }
      );
    });
  }

  #initPriceSpider() {
    const rebind = () => {
      if (window.PriceSpider?.rebind) {
        window.PriceSpider.rebind();
      }
    };

    if (window.PriceSpider?.rebind) {
      requestAnimationFrame(rebind);
      return;
    }

    const script = document.querySelector('#pricespider-script');
    if (!script) return;

    script.addEventListener('load', rebind, { once: true });
  }

  #watchSkuChanges() {
    const sku = this.dataset.variantSku;
    if (!sku) return;

    const observer = new MutationObserver(() => {
      const widget = this.querySelector('.ps-widget');
      const currentSku = widget?.getAttribute('ps-sku');
      if (!currentSku || currentSku === sku) return;

      rebindPriceSpider();
    });

    observer.observe(this, {
      attributes: true,
      subtree: true,
      attributeFilter: ['ps-sku'],
    });

    this.#abortController?.signal.addEventListener('abort', () => observer.disconnect());
  }
}

if (!customElements.get('product-details')) {
  customElements.define('product-details', ProductDetails);
}
